import React, { useMemo } from 'react';
import { Activity, Monitor, Palette, Maximize2, Cpu } from 'lucide-react';

/**
 * DisplayHealthCard
 *
 * Traffic-light health summary for a TV device's display pipeline.
 * Renders at the top of TVDetails when any diagnostics data is available.
 *
 * Four indicators:
 *   1. Signal — HDMI connected, resolution matches profile
 *   2. Color — color range + encoding assessment
 *   3. Edge — overscan / edge alignment
 *   4. Pipeline — content processing pipeline version
 *
 * Each indicator is green/amber/red/grey based on the data available.
 * When no diagnostics have been received yet (TV app hasn't sent them),
 * all indicators are grey with "Awaiting data" labels.
 */

const STATUS_COLORS = {
  good: { bg: '#dcfce7', fg: '#16a34a', dot: '#22c55e' },
  warn: { bg: '#fef9c3', fg: '#a16207', dot: '#eab308' },
  bad: { bg: '#fee2e2', fg: '#dc2626', dot: '#ef4444' },
  unknown: { bg: '#f1f5f9', fg: '#94a3b8', dot: '#cbd5e1' },
};

function assessSignal(diagnostics, edidFingerprint, profileContent) {
  if (!edidFingerprint?.width) return { status: 'unknown', label: 'No HDMI signal' };
  const res = diagnostics?.hdmi?.resolution;
  if (res) {
    if (profileContent) {
      const expected = `${profileContent.width}x${profileContent.height}`;
      // Resolution string might include refresh rate like "1920x1080p60"
      if (res.includes(`${profileContent.width}x${profileContent.height}`)) {
        return { status: 'good', label: res };
      }
      return { status: 'warn', label: `${res} (expected ${expected})` };
    }
    return { status: 'good', label: res };
  }
  if (edidFingerprint.width && edidFingerprint.height) {
    return { status: 'good', label: `${edidFingerprint.width}×${edidFingerprint.height}` };
  }
  return { status: 'unknown', label: 'Awaiting data' };
}

function assessColor(diagnostics) {
  const hdmi = diagnostics?.hdmi;
  if (!hdmi) return { status: 'unknown', label: 'Awaiting data' };

  const range = hdmi.colorRange;
  const encoding = hdmi.colorEncoding;

  if (range === 'Full' || range === 'full') {
    return { status: 'good', label: `${encoding || 'RGB'} Full range` };
  }
  if (range === 'Limited' || range === 'limited') {
    return { status: 'warn', label: `${encoding || 'YUV'} Limited range` };
  }
  if (encoding) {
    return { status: 'good', label: encoding };
  }
  return { status: 'unknown', label: 'Awaiting data' };
}

function assessEdge(diagnostics) {
  const overscan = diagnostics?.android?.overscanPercent;
  if (overscan == null) return { status: 'unknown', label: 'Awaiting data' };
  if (overscan === 0) return { status: 'good', label: 'No overscan' };
  if (overscan <= 3) return { status: 'warn', label: `${overscan}% overscan` };
  return { status: 'bad', label: `${overscan}% overscan` };
}

function assessPipeline(diagnostics) {
  const ver = diagnostics?.processingPipeline?.version;
  if (ver == null) return { status: 'unknown', label: 'Awaiting data' };
  if (ver >= 2) return { status: 'good', label: `v${ver} (zero-loss)` };
  return { status: 'warn', label: `v${ver} (legacy)` };
}

const INDICATORS = [
  { key: 'signal', label: 'Signal', icon: Monitor, assess: 'signal' },
  { key: 'color', label: 'Color', icon: Palette, assess: 'color' },
  { key: 'edge', label: 'Edge', icon: Maximize2, assess: 'edge' },
  { key: 'pipeline', label: 'Pipeline', icon: Cpu, assess: 'pipeline' },
];

const DisplayHealthCard = ({ diagnostics, edidFingerprint, profileContent }) => {
  const assessments = useMemo(() => {
    return {
      signal: assessSignal(diagnostics, edidFingerprint, profileContent),
      color: assessColor(diagnostics),
      edge: assessEdge(diagnostics),
      pipeline: assessPipeline(diagnostics),
    };
  }, [diagnostics, edidFingerprint, profileContent]);

  const hasAnyData = diagnostics != null;
  const updatedAt = diagnostics?.updatedAt;

  return (
    <div style={{
      padding: '14px 16px',
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 10,
      marginBottom: 16,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 12,
      }}>
        <Activity size={14} style={{ color: '#64748b' }} />
        <span style={{
          fontSize: 12,
          fontWeight: 700,
          color: '#475569',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          Display Health
        </span>
        {updatedAt && (
          <span style={{
            fontSize: 10,
            color: '#94a3b8',
            marginLeft: 'auto',
          }}>
            {formatRelativeTime(updatedAt)}
          </span>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
      }}>
        {INDICATORS.map(({ key, label, icon: Icon }) => {
          const { status, label: statusLabel } = assessments[key];
          const colors = STATUS_COLORS[status];
          return (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                background: colors.bg,
                borderRadius: 6,
                minWidth: 0,
              }}
            >
              <div style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: colors.dot,
                flexShrink: 0,
              }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: colors.fg,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}>
                  {label}
                </div>
                <div style={{
                  fontSize: 11,
                  color: colors.fg,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {statusLabel}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!hasAnyData && (
        <div style={{
          marginTop: 8,
          fontSize: 11,
          color: '#94a3b8',
          textAlign: 'center',
          fontStyle: 'italic',
        }}>
          Diagnostics not yet available — requires updated TV app
        </div>
      )}
    </div>
  );
};

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const ms = Date.now() - new Date(dateStr).getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export default DisplayHealthCard;
